/**
 * Shared visual atoms for the contact Kanban (avatar, deadline pill, priority marker, contact actions).
 */
import React from 'react';
import { Phone, Mail, Flag } from 'lucide-react';
import { Task } from '../../../types';
import { getAvatarGradient, getInitials, DeadlineInfo } from '../personModel';

export const PersonAvatar: React.FC<{
  name?: string;
  organization?: string;
  size?: number;
  className?: string;
  ring?: boolean;
}> = ({ name, organization, size = 32, className = '', ring = false }) => (
  <div
    className={`shrink-0 rounded-full flex items-center justify-center text-white font-bold select-none shadow-sm ${ring ? 'ring-2 ring-white dark:ring-slate-900' : ''} ${className}`}
    style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.36)), background: getAvatarGradient(organization, name) }}
    aria-hidden
  >
    {name ? getInitials(name) : '+'}
  </div>
);

const toneClasses: Record<string, string> = {
  overdue: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900',
  today: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900',
  soon: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700',
  later: 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800/40 dark:text-slate-400 dark:border-slate-700',
  'followup-due': 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800 animate-pulse',
  followup: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900'
};

export const DeadlinePill: React.FC<{ info: DeadlineInfo | null; compact?: boolean }> = ({ info, compact }) => {
  if (!info) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border font-semibold whitespace-nowrap ${compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-[11px]'} ${toneClasses[info.tone]}`}>
      {info.label}
    </span>
  );
};

const priorityMeta: Record<string, { label: string; cls: string }> = {
  critical: { label: 'Kritisch', cls: 'text-rose-600' },
  high: { label: 'Hoch', cls: 'text-rose-500' },
  medium: { label: 'Mittel', cls: 'text-amber-500' },
  low: { label: 'Niedrig', cls: 'text-sky-500' }
};

export const PriorityFlag: React.FC<{ priority?: Task['priority'] }> = ({ priority }) => {
  if (!priority || priority === 'none') return null;
  const meta = priorityMeta[priority];
  if (!meta) return null;
  return (
    <span title={`Priorität: ${meta.label}`} className={`inline-flex items-center ${meta.cls}`}>
      <Flag size={12} fill="currentColor" />
    </span>
  );
};

export const ContactQuickActions: React.FC<{ phone?: string; email?: string; size?: number }> = ({ phone, email, size = 13 }) => {
  if (!phone && !email) return null;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div className="flex items-center gap-0.5" onPointerDown={stop}>
      {phone && (
        <a href={`tel:${phone}`} onClick={stop} title={phone} className="p-1 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors">
          <Phone size={size} />
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} onClick={stop} title={email} className="p-1 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors">
          <Mail size={size} />
        </a>
      )}
    </div>
  );
};

export function countNotes(t: Task): number {
  if (!t.notes) return 0;
  if (Array.isArray(t.notes)) return t.notes.length;
  return String(t.notes).trim() ? 1 : 0;
}
